import { AxiosError, AxiosResponse } from 'axios';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from 'i18next';

import {
  mergeDeviceCharacteristicGroupDetail,
  mergeDeviceDetail,
  setDeliveryPointGDSDevicesList,
  setDeviceCharacteristicGroupDetail,
  setDeviceDetail,
  setDeviceList,
  setDevicesForPlanning,
  setIsDeviceListForHxDashboardReady,
  setIsDeviceListReady
} from 'features/eai/eaiSlice';
import {
  createDeleteHeaderData,
  createGetHeaderData,
  createHeaderData,
  createPutHeaderData,
  createQueryParams,
  handlePreconditionFailed
} from 'utils/restUtils';
import { apiClient } from 'services/restService';
import {
  CreateDeviceCharacteristicGroupParams,
  CreateDeviceRequestBody,
  DeviceCharacteristicGroupDetailType,
  DeviceDetailInterface,
  GetDeviceListRequestParams,
  UpdateDeviceCharacteristicGroupCharacteristicsRequestBody,
  UpdateDeviceRequestBody
} from 'features/eai/eaiTypes';
import { openSnackbar } from 'features/app/appSlice';
import { AppThunk } from 'models/store';
import { HttpHeaders } from 'models';
import { EaiEtagEntityList } from 'models/etag';
import { GET_LIST_WITHOUT_PAGINATION } from 'utils/constants';
import { logError } from 'services/Logging';

// ==============================|| DEVICE THUNKS ||============================== //

const devicePrefix = '/eai/device';

export const getDeviceList =
  ({
    search,
    installationStatuses,
    size,
    page,
    deviceRoles,
    deliveryPointId,
    localDistributionSystemId,
    sort,
    balancingGroupId,
    devicesForPlanning,
    devicesForReservedCapacity
  }: GetDeviceListRequestParams): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<
        | typeof setDeviceList
        | typeof setDevicesForPlanning
        | typeof setIsDeviceListReady
        | typeof setIsDeviceListForHxDashboardReady
      >
    >
  ) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    const queryParams = createQueryParams({
      search,
      installationStatuses,
      size,
      page,
      deviceRoles,
      deliveryPointId,
      localDistributionSystemId,
      sort,
      balancingGroupId
    });

    apiClient
      .get(`${devicePrefix}${queryParams}`, { headers })
      .then((response: AxiosResponse) => {
        devicesForPlanning
          ? dispatch(setDevicesForPlanning(response.data))
          : dispatch(setDeviceList(response.data));
      })
      .catch((error: AxiosError) => {
        logError(error);
      })
      .finally(() => {
        devicesForPlanning || devicesForReservedCapacity
          ? dispatch(setIsDeviceListReady(true))
          : dispatch(setIsDeviceListForHxDashboardReady(true));
      });
  };

export const createDevice =
  (
    requestBody: CreateDeviceRequestBody,
    callback?: (id: string) => void
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*');

    apiClient
      .post(`${devicePrefix}`, { ...requestBody }, { headers })
      .then((response: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.createDevice'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback?.(response.data.deviceId);
      })
      .catch((error: AxiosError) => logError(error));
  };

export const fetchDeviceDetail = async (deviceId: string | undefined) => {
  return new Promise<DeviceDetailInterface>((resolve, reject) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    apiClient
      .get(`/eai/device/detail/${deviceId}`, {
        headers,
        data: { etagEntity: EaiEtagEntityList.DEVICE }
      })
      .then((response: AxiosResponse) => {
        resolve(response.data as DeviceDetailInterface);
      })
      .catch((error: AxiosError) => {
        logError(error);
        reject(error);
      });
  });
};

export const getAsyncDeviceDetail =
  (id: string): AppThunk<Promise<DeviceDetailInterface>> =>
  () =>
    fetchDeviceDetail(id);

export const fetchAndSetDeviceDetail =
  (deviceId: string | undefined, mergeData?: boolean): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof setDeviceDetail | typeof mergeDeviceDetail>
    >
  ) => {
    fetchDeviceDetail(deviceId)
      .then((result) => {
        mergeData
          ? dispatch(mergeDeviceDetail([result]))
          : dispatch(setDeviceDetail([result]));
      })
      .catch((error: AxiosError) => logError(error));
  };

export const getDevicesForDeliveryPointGDS =
  (gdsId: string): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof setDeliveryPointGDSDevicesList>>) => {
    const headers: HttpHeaders = createGetHeaderData(
      true,
      'application/hal+json'
    );

    const queryParams = createQueryParams({
      gdsId: gdsId,
      size: GET_LIST_WITHOUT_PAGINATION
    });

    apiClient
      .get(`${devicePrefix}/${queryParams}`, {
        headers
      })
      .then((response: AxiosResponse) => {
        dispatch(setDeliveryPointGDSDevicesList(response.data));
      });
  };

export const updateDevice =
  (
    requestBody: UpdateDeviceRequestBody,
    deviceId?: string,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeviceDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `eai/device/${deviceId || ''}/update`,
        { ...requestBody },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(fetchAndSetDeviceDetail(deviceId));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.updateDevice'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback?.();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetDeviceDetail(deviceId)),
          () =>
            dispatch(updateDevice(requestBody, deviceId, undefined, callback))
        );
        logError(error);
      });
  };

export const createDeviceCharacteristicGroup =
  ({
    deviceId,
    requestBody,
    callback
  }: CreateDeviceCharacteristicGroupParams): AppThunk =>
  async (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createPutHeaderData(true, '*/*');
    const config = { headers };
    const url = `eai/device/${deviceId}/characteristic-groups`;

    try {
      const response: AxiosResponse<DeviceCharacteristicGroupDetailType> =
        await apiClient.put(url, requestBody, config);

      dispatch(
        openSnackbar({
          open: true,
          message: i18n.t(
            'module.eai.messages.createDeviceCharacteristicGroup'
          ),
          variant: 'alert',
          alert: {
            color: 'success'
          },
          close: false
        })
      );

      callback(response.data.deviceCharacteristicGroupId.toString());
    } catch (error) {
      logError(error);
    }
  };

export const fetchDeviceCharacteristicGroupDetail = (
  deviceId: string,
  characteristicGroupId: string
) => {
  return new Promise<DeviceCharacteristicGroupDetailType>((resolve, reject) => {
    const headers = createGetHeaderData(true, 'application/hal+json');
    const config = { headers };
    const path = `eai/device/${deviceId}/characteristic-groups/detail/${characteristicGroupId}`;

    apiClient
      .get(path, config)
      .then((response: AxiosResponse<DeviceCharacteristicGroupDetailType>) => {
        resolve(response.data);
      })
      .catch((error: AxiosError) => {
        logError(error);
        reject(error);
      });
  });
};

export const fetchAndSetDeviceCharacteristicGroupDetail = (
  dispatch: ThunkDispatch<any, any, any>,
  deviceId: string,
  deviceGroupId: string | bigint,
  mergeData?: boolean
) => {
  fetchDeviceCharacteristicGroupDetail(deviceId, deviceGroupId.toString())
    .then((deviceCharacteristicGroupDetail) => {
      mergeData
        ? dispatch(
            mergeDeviceCharacteristicGroupDetail([
              deviceCharacteristicGroupDetail
            ])
          )
        : dispatch(
            setDeviceCharacteristicGroupDetail([
              deviceCharacteristicGroupDetail
            ])
          );
    })
    .catch((error: AxiosError) => logError(error));
};

type UpdateDeviceCharacteristicGroupParams = {
  callback?: () => void;
  deviceId: string;
  etag?: string;
  characteristicGroupId: bigint | string;
  mergeData?: boolean;
  requestBody: UpdateDeviceCharacteristicGroupCharacteristicsRequestBody;
};

export const updateDeviceCharacteristicGroup =
  ({
    callback,
    deviceId,
    etag,
    characteristicGroupId,
    mergeData,
    requestBody
  }: UpdateDeviceCharacteristicGroupParams): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeviceDetail>
    >
  ) => {
    const headers = createHeaderData(true, '*/*', etag);
    const path = `eai/device/${deviceId}/characteristic-groups/${characteristicGroupId}/characteristics`;
    const config = { headers };

    apiClient
      .patch(path, requestBody, config)
      .then((_response: AxiosResponse) => {
        dispatch(fetchAndSetDeviceDetail(deviceId, mergeData));

        fetchAndSetDeviceCharacteristicGroupDetail(
          dispatch,
          deviceId,
          characteristicGroupId,
          mergeData
        );

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t(
              'module.eai.messages.updateDeviceCharacteristicGroupCharacteristics'
            ),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback?.();
      })
      .catch((error: AxiosError) => {
        const forceUpdateCallback = () =>
          dispatch(
            updateDeviceCharacteristicGroup({
              callback,
              characteristicGroupId: characteristicGroupId,
              deviceId,
              requestBody
            })
          );
        const updateDataCallback = () => {
          dispatch(fetchAndSetDeviceDetail(deviceId));
          fetchAndSetDeviceCharacteristicGroupDetail(
            dispatch,
            deviceId,
            characteristicGroupId
          );
        };

        handlePreconditionFailed(
          error,
          updateDataCallback,
          forceUpdateCallback
        );
        logError(error);
      });
  };

export const deleteDevice =
  (deviceId: string | bigint | undefined, callback?: () => void): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createDeleteHeaderData(true, '*/*');

    apiClient
      .delete(`eai/device/${deviceId || ''}/delete`, { headers })
      .then((_: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.deleteDevice'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback?.();
      })
      .catch((error: AxiosError) => logError(error));
  };

export const assignGDS =
  (
    deviceId: string,
    gdsId: string | null,
    etag?: string,
    callback?: () => void
  ): AppThunk =>
  (
    dispatch: Dispatch<
      ReturnType<typeof openSnackbar | typeof fetchAndSetDeviceDetail>
    >
  ) => {
    const headers: HttpHeaders = createHeaderData(true, '*/*', etag);

    apiClient
      .patch(
        `eai/device/${deviceId}/gateway-dispatcher-system`,
        { gdsId: gdsId },
        { headers }
      )
      .then((_response: AxiosResponse) => {
        dispatch(fetchAndSetDeviceDetail(deviceId));

        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.assignGDS'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback?.();
      })
      .catch((error: AxiosError) => {
        handlePreconditionFailed(
          error,
          () => dispatch(fetchAndSetDeviceDetail(deviceId)),
          () => dispatch(assignGDS(deviceId, gdsId, undefined, callback))
        );
        logError(error);
      });
  };

export const removeDeviceCharacteristicGroup =
  (
    deviceId: string,
    characteristicGroupId: string | bigint,
    callback?: () => void
  ): AppThunk =>
  (dispatch: Dispatch<ReturnType<typeof openSnackbar>>) => {
    const headers: HttpHeaders = createDeleteHeaderData(true, '*/*');
    const queryParams = createQueryParams({
      characteristicGroupId: characteristicGroupId
    });

    apiClient
      .delete(`eai/device/${deviceId}/characteristic-groups${queryParams}`, {
        headers
      })
      .then((_: AxiosResponse) => {
        dispatch(
          openSnackbar({
            open: true,
            message: i18n.t('module.eai.messages.deleteDeviceGroup'),
            variant: 'alert',
            alert: {
              color: 'success'
            },
            close: false
          })
        );

        callback?.();
      })
      .catch((error: AxiosError) => logError(error));
  };
